import { useState } from "react";
import { X, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { T } from "../../ui/tokens";
import { fmt, fmtDate } from "../../lib/format";
import { useCompararContas, useFundirContas } from "./useExtrato";
import type { ComparacaoContas, LadoComparacao, MovimentoResumido } from "./types";

interface Props {
  empresa?: string;
  contaIdA: number;
  contaIdB: number;
  onFechar: () => void;
}

const rotulo: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: T.textLight,
  textTransform: "uppercase", letterSpacing: ".06em",
};

function Lado({ lado, destaque }: { lado: LadoComparacao; destaque: boolean }) {
  return (
    <div style={{
      border: `2px solid ${destaque ? T.blue : T.border}`, borderRadius: 11,
      padding: "13px 14px", background: destaque ? T.blueBg : T.surface, minWidth: 0,
    }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>
        {lado.banco?.trim() || "Conta por configurar"}
      </div>
      <div style={{ fontSize: 11.5, fontFamily: "ui-monospace, monospace", color: T.textMid, marginTop: 3, wordBreak: "break-all" }}>
        {lado.conta}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "5px 10px", marginTop: 10, fontSize: 12, color: T.textMid }}>
        <div>Movimentos</div><div style={{ fontWeight: 700, color: T.text }}>{lado.total_movimentos}</div>
        <div>Extratos</div><div style={{ fontWeight: 700, color: T.text }}>{lado.total_importacoes}</div>
        <div>Período</div>
        <div style={{ fontWeight: 700, color: T.text }}>
          {lado.periodo_inicio ? `${fmtDate(lado.periodo_inicio)} — ${fmtDate(lado.periodo_fim || lado.periodo_inicio)}` : "—"}
        </div>
        <div>Já conciliados</div><div style={{ fontWeight: 700, color: T.text }}>{lado.com_decisao_humana}</div>
        {lado.saldo != null && (<><div>Saldo</div><div style={{ fontWeight: 700, color: T.text }}>{fmt(lado.saldo)}</div></>)}
      </div>
      {!!lado.importacoes.length && (
        <div style={{ marginTop: 10, paddingTop: 9, borderTop: `1px solid ${T.borderSoft}` }}>
          <div style={{ ...rotulo, marginBottom: 5 }}>Origem dos dados</div>
          {lado.importacoes.slice(0, 4).map((imp) => (
            <div key={imp.id} style={{ fontSize: 11.5, color: T.textMid, marginBottom: 3 }}>
              {imp.arquivo_nome || imp.import_id} <span style={{ color: T.textLight }}>({imp.fonte})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListaMovimentos({ titulo, movimentos, total }: { titulo: string; movimentos: MovimentoResumido[]; total: number }) {
  if (!total) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ ...rotulo, marginBottom: 6 }}>{titulo} ({total})</div>
      {movimentos.map((m, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, color: T.textMid, padding: "3px 0" }}>
          <span style={{ whiteSpace: "nowrap" }}>{fmtDate(m.data_mov)}</span>
          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.descricao}</span>
          <span style={{ fontWeight: 700, color: m.valor < 0 ? T.red : T.green, whiteSpace: "nowrap" }}>{fmt(m.valor)}</span>
        </div>
      ))}
      {total > movimentos.length && (
        <div style={{ fontSize: 11.5, color: T.textLight, marginTop: 3 }}>… e mais {total - movimentos.length}.</div>
      )}
    </div>
  );
}

/**
 * Painel "Comparar contas" — a evidência antes de qualquer decisão.
 *
 * A fusão nunca está a um clique de distância: primeiro mostra-se o que as
 * duas contas têm, depois um plano (dry-run, que não escreve nada), e só
 * então aparece a ação de fundir. Remover movimentos duplicados exige uma
 * autorização à parte, porque é a única parte da operação que apaga dados.
 */
export default function PainelCompararContas({ empresa, contaIdA, contaIdB, onFechar }: Props) {
  const { data, isLoading, isError } = useCompararContas(empresa, contaIdA, contaIdB);
  const fundir = useFundirContas(empresa);
  const [plano, setPlano] = useState<Record<string, unknown> | null>(null);
  const [destino, setDestino] = useState<number | null>(null);
  const [autorizaRemover, setAutorizaRemover] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<string | null>(null);

  const comparacao = data as ComparacaoContas | undefined;
  const destinoEfetivo = destino ?? comparacao?.sugestao_manter ?? null;
  const origemEfetiva = destinoEfetivo == null
    ? null
    : destinoEfetivo === contaIdA ? contaIdB : contaIdA;

  const verPlano = () => {
    if (origemEfetiva == null || destinoEfetivo == null) return;
    setErro(null);
    fundir.mutate(
      { origem: origemEfetiva, destino: destinoEfetivo, confirmar: false },
      {
        onSuccess: (d) => setPlano((d as { plano?: Record<string, unknown> })?.plano ?? null),
        onError: (e: Error) => setErro(e.message),
      },
    );
  };

  const executar = () => {
    if (origemEfetiva == null || destinoEfetivo == null) return;
    setErro(null);
    fundir.mutate(
      { origem: origemEfetiva, destino: destinoEfetivo, confirmar: true, removerDuplicados: autorizaRemover },
      {
        onSuccess: (d) => {
          const r = (d as { resultado?: Record<string, number> })?.resultado;
          setFeito(
            duplicadoExato
              ? `Conta duplicada eliminada: ${r?.movimentos_duplicados_removidos ?? 0} movimento(s) repetidos removidos.`
              : `Fusão concluída: ${r?.movimentos_movidos ?? 0} movimento(s) movidos, ` +
                `${r?.movimentos_duplicados_removidos ?? 0} duplicado(s) removidos.`
          );
          setPlano(null);
        },
        onError: (e: Error) => setErro(e.message),
      },
    );
  };

  const nDuplicados = ((plano?.duplicados as unknown[]) || []).length;
  const nBloqueios = ((plano?.bloqueios as unknown[]) || []).length;
  const podeExecutar = !!plano && nBloqueios === 0 && (nDuplicados === 0 || autorizaRemover);
  const duplicadoExato = !!comparacao?.historico_duplicado;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Comparar contas bancárias"
      onClick={onFechar}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: T.surface, borderRadius: 14, width: "min(760px, 100%)", maxHeight: "90vh", overflowY: "auto", border: `1px solid ${T.border}`, boxShadow: "0 20px 60px rgba(16,24,40,.24)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: "18px 20px 10px" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>Comparar contas</div>
            <div style={{ fontSize: 12, color: T.textMid, marginTop: 3 }}>
              Duas contas com números compatíveis — confirma se são a mesma antes de decidir.
            </div>
          </div>
          <button onClick={onFechar} aria-label="Fechar" style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, padding: 4, lineHeight: 0 }}>
            <X size={17} />
          </button>
        </div>

        {isLoading ? (
          <div style={{ padding: "50px 20px", textAlign: "center", color: T.textLight, fontSize: 13 }}>A comparar…</div>
        ) : isError || !comparacao ? (
          <div style={{ padding: "50px 20px", textAlign: "center", color: T.red, fontSize: 13 }}>Não foi possível comparar as contas.</div>
        ) : (
          <div style={{ padding: "0 20px 18px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
              <Lado lado={comparacao.a} destaque={destinoEfetivo === comparacao.a.conta_id} />
              <Lado lado={comparacao.b} destaque={destinoEfetivo === comparacao.b.conta_id} />
            </div>

            <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: T.bg, border: `1px solid ${T.borderSoft}` }}>
              <div style={{ fontSize: 12.5, color: T.textMid }}>
                {duplicadoExato
                  ? "Estas duas entradas representam a mesma conta e repetem o mesmo histórico."
                  : `Os números ${comparacao.numeros_coincidem ? "são compatíveis" : "não coincidem"}.`}
                {" "}<strong style={{ color: T.text }}>{comparacao.movimentos_em_comum}</strong> movimento(s) existem nas duas,
                {" "}<strong style={{ color: T.text }}>{comparacao.so_em_a}</strong> só na primeira e
                {" "}<strong style={{ color: T.text }}>{comparacao.so_em_b}</strong> só na segunda.
              </div>
              <ListaMovimentos titulo="Em ambas" movimentos={comparacao.exemplos_em_comum} total={comparacao.movimentos_em_comum} />
              <ListaMovimentos titulo="Só na primeira" movimentos={comparacao.exemplos_so_em_a} total={comparacao.so_em_a} />
              <ListaMovimentos titulo="Só na segunda" movimentos={comparacao.exemplos_so_em_b} total={comparacao.so_em_b} />
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ ...rotulo, marginBottom: 6 }}>Conta a manter</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {[comparacao.a, comparacao.b].map((l) => (
                  <button
                    key={l.conta_id}
                    onClick={() => { setDestino(l.conta_id); setPlano(null); }}
                    style={{
                      padding: "8px 13px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                      border: `1px solid ${destinoEfetivo === l.conta_id ? T.blue : T.border}`,
                      background: destinoEfetivo === l.conta_id ? T.blueBg : T.surface,
                      color: destinoEfetivo === l.conta_id ? T.blue : T.textMid,
                    }}
                  >
                    {l.banco?.trim() || "Por configurar"} · {l.conta}
                    {comparacao.sugestao_manter === l.conta_id && " (sugerida)"}
                  </button>
                ))}
              </div>
            </div>

            {feito ? (
              <div style={{ marginTop: 14, padding: "11px 13px", borderRadius: 9, background: T.greenBg, border: `1px solid ${T.greenBorder}`, color: T.green, fontSize: 12.5, fontWeight: 700 }}>
                {feito}
              </div>
            ) : (
              <>
                <div style={{ marginTop: 14, display: "flex", gap: 9, flexWrap: "wrap", alignItems: "center" }}>
                  <button
                    onClick={verPlano}
                    disabled={fundir.isPending}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 15px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 12.5, fontWeight: 700, cursor: fundir.isPending ? "default" : "pointer" }}
                  >
                    {fundir.isPending && <Loader2 size={13} style={{ animation: "spin-extrato 0.8s linear infinite" }} />}
                    {duplicadoExato ? "Ver o que será eliminado" : "Ver o que a fusão faria"}
                  </button>
                  {plano != null && (
                    <span style={{ fontSize: 12, color: T.textMid, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <ArrowRight size={13} />
                      {duplicadoExato
                        ? `${nDuplicados} movimento(s) repetidos serão removidos`
                        : `${String((plano.mover as unknown[])?.length ?? 0)} movimento(s) mudariam de conta`}
                    </span>
                  )}
                </div>

                {plano != null && (
                  <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface }}>
                    <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.6 }}>
                      {duplicadoExato ? "Remover a entrada duplicada, preservando a conta escolhida. " : <>Mover <strong style={{ color: T.text }}>{String((plano.mover as unknown[])?.length ?? 0)}</strong> movimento(s) e </>}
                      {" "}<strong style={{ color: T.text }}>{String((plano.importacoes_a_mover as unknown[])?.length ?? 0)}</strong> importação(ões).
                      {nDuplicados > 0 && <> Existem <strong style={{ color: T.amber }}>{nDuplicados}</strong> movimento(s) da conta de origem que já existem na de destino.</>}
                      {" "}A conta ficaria com <strong style={{ color: T.text }}>{String(plano.total_apos_fusao ?? "?")}</strong> movimentos.
                    </div>

                    {nBloqueios > 0 && (
                      <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: T.redBg, border: `1px solid ${T.redBorder}`, color: T.red, fontSize: 12.5, display: "flex", gap: 8 }}>
                        <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>
                          {nBloqueios} movimento(s) duplicados já têm conciliação feita. Removê-los apagaria esse trabalho —
                          resolve-os primeiro; a fusão fica bloqueada até lá.
                        </span>
                      </div>
                    )}

                    {nDuplicados > 0 && nBloqueios === 0 && (
                      <label style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: T.textMid, cursor: "pointer" }}>
                        <input type="checkbox" checked={autorizaRemover} onChange={(e) => setAutorizaRemover(e.target.checked)} style={{ marginTop: 2 }} />
                        <span>Autorizo remover os {nDuplicados} movimento(s) duplicados da conta de origem.</span>
                      </label>
                    )}

                    <button
                      onClick={executar}
                      disabled={!podeExecutar || fundir.isPending}
                      style={{
                        marginTop: 12, padding: "9px 17px", borderRadius: 8, border: "none",
                        background: podeExecutar && !fundir.isPending ? T.blue : T.textLight,
                        color: "#fff", fontSize: 12.5, fontWeight: 700,
                        cursor: podeExecutar && !fundir.isPending ? "pointer" : "default",
                      }}
                    >
                      {duplicadoExato ? "Eliminar conta duplicada" : "Fundir contas"}
                    </button>
                  </div>
                )}
              </>
            )}

            {erro && (
              <div style={{ marginTop: 12, padding: "10px 13px", borderRadius: 9, background: T.redBg, border: `1px solid ${T.redBorder}`, color: T.red, fontSize: 12.5 }}>
                {erro}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
